export const AP_BIO_WORKFLOW_LEARNING = 'learning';
export const AP_BIO_WORKFLOW_FRQ = 'frq-coach';
export const AP_BIO_WORKFLOW_DATA = 'data-experiment-help';
export const AP_BIO_WORKFLOW_PRACTICE = 'practice-generator';
export const AP_BIO_WORKFLOW_REVISION = 'revision-plan';

export const AP_BIO_WORKFLOW_OPTIONS = [
  {
    id: AP_BIO_WORKFLOW_LEARNING,
    label: 'Learning',
    shortLabel: 'Learn',
    description: 'Break down tough AP Biology ideas clearly and step by step.',
  },
  {
    id: AP_BIO_WORKFLOW_FRQ,
    label: 'FRQ Coach',
    shortLabel: 'FRQ Coach',
    description: 'Paste a prompt and your answer to see what earns credit and what misses.',
  },
  {
    id: AP_BIO_WORKFLOW_DATA,
    label: 'Data + Experiment Help',
    shortLabel: 'Data Help',
    description: 'Work through graphs, variables, controls, conclusions, and experiment design.',
  },
  {
    id: AP_BIO_WORKFLOW_PRACTICE,
    label: 'Practice Generator',
    shortLabel: 'Practice',
    description: 'Generate fresh AP-style MCQs and FRQs by unit, topic, and difficulty.',
  },
  {
    id: AP_BIO_WORKFLOW_REVISION,
    label: 'Study Plan',
    shortLabel: 'Study',
    description: 'Turn weak topics into a focused AP Biology study plan.',
  },
];

export const AP_BIO_TUTOR_WORKFLOW_OPTIONS = AP_BIO_WORKFLOW_OPTIONS.filter(
  (workflow) => ![AP_BIO_WORKFLOW_PRACTICE, AP_BIO_WORKFLOW_REVISION].includes(workflow.id)
);

export const AP_BIO_DATA_FOCUS_OPTIONS = [
  { value: 'interpret_graph', label: 'Interpret graph' },
  { value: 'variables_controls', label: 'Variables / controls' },
  { value: 'evaluate_conclusion', label: 'Evaluate conclusion' },
  { value: 'improve_experiment', label: 'Improve experiment' },
];

export const AP_BIO_PRACTICE_TYPE_OPTIONS = [
  { value: 'frq', label: 'FRQ' },
  { value: 'mcq', label: 'MCQ' },
];

export const AP_BIO_PRACTICE_DIFFICULTY_OPTIONS = [
  { value: 'support', label: 'Support' },
  { value: 'exam', label: 'Exam' },
  { value: 'challenge', label: 'Challenge' },
];

export const AP_BIO_UNIT_OPTIONS = [
  { value: '', label: 'Any unit' },
  { value: 'Unit 1', label: 'Unit 1: Chemistry of Life' },
  { value: 'Unit 2', label: 'Unit 2: Cell Structure and Function' },
  { value: 'Unit 3', label: 'Unit 3: Cellular Energetics' },
  { value: 'Unit 4', label: 'Unit 4: Cell Communication and Cell Cycle' },
  { value: 'Unit 5', label: 'Unit 5: Heredity' },
  { value: 'Unit 6', label: 'Unit 6: Gene Expression and Regulation' },
  { value: 'Unit 7', label: 'Unit 7: Natural Selection' },
  { value: 'Unit 8', label: 'Unit 8: Ecology' },
];

export const AP_BIO_FORM_DEFAULTS = Object.freeze({
  questionText: '',
  answerText: '',
  questionLocator: '',
  questionAttachments: [],
  answerAttachments: [],
  rubricContext: '',
  analysisFocus: AP_BIO_DATA_FOCUS_OPTIONS[0].value,
  practiceQuestionType: AP_BIO_PRACTICE_TYPE_OPTIONS[0].value,
  practiceUnit: '',
  practiceDifficulty: AP_BIO_PRACTICE_DIFFICULTY_OPTIONS[1].value,
  revisionDays: '',
  revisionMinutes: '',
});

const LEGACY_WORKFLOW_MAP = {
  answer: AP_BIO_WORKFLOW_LEARNING,
  'similar-question': AP_BIO_WORKFLOW_PRACTICE,
  'generate-question': AP_BIO_WORKFLOW_PRACTICE,
  rubric: AP_BIO_WORKFLOW_FRQ,
  'mark-scheme': AP_BIO_WORKFLOW_FRQ,
  similar_question: AP_BIO_WORKFLOW_PRACTICE,
  question_generation_seed: AP_BIO_WORKFLOW_PRACTICE,
};

const WORKFLOW_META = AP_BIO_WORKFLOW_OPTIONS.reduce((accumulator, workflow) => {
  accumulator[workflow.id] = workflow;
  return accumulator;
}, {});

const PRIMARY_INPUT_META = {
  [AP_BIO_WORKFLOW_LEARNING]: {
    label: 'What are you stuck on?',
    placeholder: 'Tell me what you are stuck on in AP Biology and I will break it down clearly...',
    helper: 'Best for concept confusion, diagrams, vocabulary, and step-by-step understanding.',
  },
  [AP_BIO_WORKFLOW_FRQ]: {
    label: 'Question prompt',
    placeholder: 'Paste the FRQ prompt or describe what the question is asking...',
    helper: 'Pair the official prompt with your draft answer for the strongest coaching.',
  },
  [AP_BIO_WORKFLOW_DATA]: {
    label: 'What do you want help with?',
    placeholder: 'Paste the graph, experiment, or data question and tell me where you are stuck...',
    helper: 'Use this for graphs, variables, controls, claims, and experimental reasoning.',
  },
  [AP_BIO_WORKFLOW_PRACTICE]: {
    label: 'Topic or weakness (optional)',
    placeholder: 'e.g. enzyme inhibition, osmosis, population genetics, Hardy-Weinberg...',
    helper: 'Leave this blank if you just want a fresh question from the selected unit and difficulty.',
  },
  [AP_BIO_WORKFLOW_REVISION]: {
    label: 'Weak topics',
    placeholder: 'List the AP Biology topics you want the plan to target...',
    helper: 'Separate topics with commas or new lines. Recent chat history will still be used as context.',
  },
};

const WORKFLOW_COPY = {
  [AP_BIO_WORKFLOW_LEARNING]: {
    welcomeTitle: 'Learning: AP Biology Tutor',
    welcomeSubtitle: 'Get clear, grounded AP Biology explanations with diagrams, experiments, and textbook support when useful.',
  },
  [AP_BIO_WORKFLOW_FRQ]: {
    welcomeTitle: 'FRQ Coach: AP Biology Tutor',
    welcomeSubtitle: 'See what your answer earns, what it misses, and how to improve it into a stronger AP response.',
  },
  [AP_BIO_WORKFLOW_DATA]: {
    welcomeTitle: 'Data + Experiment Help',
    welcomeSubtitle: 'Untangle graphs, variables, controls, conclusions, and experiment design without guessing.',
  },
  [AP_BIO_WORKFLOW_PRACTICE]: {
    welcomeTitle: 'Practice Generator',
    welcomeSubtitle: 'Create fresh AP-style MCQs and FRQs by unit, difficulty, or weakness in seconds.',
  },
  [AP_BIO_WORKFLOW_REVISION]: {
    welcomeTitle: 'Study Plan',
    welcomeSubtitle: 'Turn your weak AP Biology topics into a realistic study sequence with the right practice mix.',
  },
};

const AP_BIO_INLINE_CITATION_PATTERN = /\s*(?:\[(?:\s*S\d+(?:\s*(?:,|-)\s*S?\d+)*\s*)\]|\(\s*S\d+(?:\s*(?:,|-)\s*S?\d+)*\s*\))/gi;
const AP_BIO_TRAILING_CITATION_FRAGMENT_PATTERN = /\s*(?:\[(?:\s*S\d*(?:\s*(?:,|-)\s*S?\d*)*)?|\((?:\s*S\d*(?:\s*(?:,|-)\s*S?\d*)*)?)$/i;
const AP_BIO_UNWANTED_SECTION_LINE_PATTERN = /^\s*sources?(?:\s+of)?\s+inspiration(?:\s*[:\-–—].*)?\s*$/i;
const AP_BIO_FOLLOW_UP_CUE_PATTERNS = [
  /\beli5\b/i,
  /\bplain english\b/i,
  /\b(?:explain|say|put|break|walk)\b[\s\S]*\b(?:simple|simpler|simply|easier|easy)\b/i,
  /\b(?:explain|break)\b[\s\S]*\b(?:5|five|7|seven)\s*(?:year|yr)[-\s]*old\b/i,
  /\bdumb it down\b/i,
  /\bstill confused\b/i,
  /\b(?:explain|rephrase|reword|say)\b[\s\S]*\b(?:again|another way)\b/i,
];
const AP_BIO_GENERIC_FOLLOW_UP_TOKENS = new Set([
  'a', 'again', 'am', 'an', 'and', 'another', 'any', 'as', 'at', 'be', 'break', 'can', 'confused',
  'down', 'dumb', 'easy', 'easier', 'eli5', 'english', 'explain', 'for', 'five', 'i', 'im', 'in',
  'it', 'kid', 'like', 'make', 'me', 'more', 'of', 'ok', 'okay', 'old', 'plain', 'please', 'put',
  'rephrase', 'reword', 'say', 'seven', 'simple', 'simpler', 'simply', 'so', 'still', 'step',
  'super', 'terms', 'that', 'the', 'this', 'to', 'understand', 'way', 'year', 'yr',
]);

function isApBioRemovedArtifactLabel(value) {
  return AP_BIO_UNWANTED_SECTION_LINE_PATTERN.test(String(value || '').trim());
}

function tokenizeApBioPrompt(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);
}

function isContextDependentApBioFollowUp(query) {
  const trimmedQuery = String(query || '').trim();
  if (!trimmedQuery) return false;
  if (!AP_BIO_FOLLOW_UP_CUE_PATTERNS.some((pattern) => pattern.test(trimmedQuery))) {
    return false;
  }
  const meaningfulTokens = tokenizeApBioPrompt(trimmedQuery)
    .filter((token) => !AP_BIO_GENERIC_FOLLOW_UP_TOKENS.has(token))
    .filter((token) => !/^\d+$/.test(token));
  return meaningfulTokens.length === 0;
}

function buildApBioFollowUpContext(conversationHistory = []) {
  const priorConversation = [...(Array.isArray(conversationHistory) ? conversationHistory : [])]
    .reverse()
    .find((conversation) => {
      const question = String(conversation?.question || '').trim();
      const answer = stripApBioInlineCitations(conversation?.answer || '');
      return Boolean(question && answer);
    });

  if (!priorConversation) return null;

  const previousQuestion = String(priorConversation.question || '').trim().slice(0, 600);
  const previousAnswer = stripApBioInlineCitations(priorConversation.answer || '').slice(0, 1000);
  if (!previousQuestion && !previousAnswer) return null;

  return {
    previousQuestion,
    previousAnswer,
  };
}

function stripApBioArtifactSections(value) {
  if (value == null) return '';

  const lines = String(value).split(/\r?\n/);
  const cleanedLines = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (!isApBioRemovedArtifactLabel(line)) {
      cleanedLines.push(line);
      continue;
    }

    index += 1;
    while (index < lines.length && String(lines[index] || '').trim()) {
      index += 1;
    }
  }

  return cleanedLines.join('\n');
}

export function normalizeApBioWorkflow(value, fallback = AP_BIO_WORKFLOW_LEARNING) {
  const normalized = String(value || '').trim().toLowerCase();
  if (WORKFLOW_META[normalized]) return normalized;
  return LEGACY_WORKFLOW_MAP[normalized] || fallback;
}

export function stripApBioInlineCitations(value) {
  if (value == null) return '';

  return stripApBioArtifactSections(String(value))
    .replace(AP_BIO_INLINE_CITATION_PATTERN, '')
    .replace(AP_BIO_TRAILING_CITATION_FRAGMENT_PATTERN, '')
    .replace(/[ \t]+([,.;:!?])/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function getApBioWorkflowMeta(workflow) {
  return WORKFLOW_META[normalizeApBioWorkflow(workflow)];
}

export function getApBioPrimaryInputMeta(workflow) {
  return PRIMARY_INPUT_META[normalizeApBioWorkflow(workflow)] || PRIMARY_INPUT_META[AP_BIO_WORKFLOW_LEARNING];
}

export function getApBioWorkflowCopy(workflow) {
  return WORKFLOW_COPY[normalizeApBioWorkflow(workflow)] || WORKFLOW_COPY[AP_BIO_WORKFLOW_LEARNING];
}

export function getApBioDataFocusLabel(value) {
  return AP_BIO_DATA_FOCUS_OPTIONS.find((item) => item.value === value)?.label || AP_BIO_DATA_FOCUS_OPTIONS[0].label;
}

export function splitApBioTopics(value) {
  return String(value || '')
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function toOptionalPositiveInt(value) {
  const parsed = Number.parseInt(String(value || '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function normalizeApBioConversation(conversation, fallbackWorkflow = AP_BIO_WORKFLOW_LEARNING) {
  if (!conversation || typeof conversation !== 'object') return null;
  const generatedQuestionHistory = Array.isArray(conversation.generatedQuestionHistory)
    ? conversation.generatedQuestionHistory.filter(Boolean)
    : [];
  return {
    ...conversation,
    answer: stripApBioInlineCitations(conversation.answer || ''),
    questionPayload: normalizeApBioQuestionPayload(conversation.questionPayload || conversation.question_payload || null),
    questionType: String(conversation.questionType || conversation.question_type || '').trim().toLowerCase() || null,
    activeQuestionDocument: conversation.activeQuestionDocument || null,
    generatedQuestionHistory,
    workflow: normalizeApBioWorkflow(
      conversation.workflow || conversation.actionType || conversation.retrievalMode,
      fallbackWorkflow,
    ),
  };
}

export function normalizeApBioQuestionPayload(value) {
  if (!value || typeof value !== 'object') return null;

  const question = stripApBioInlineCitations(value.question || '');
  if (!question) return null;

  const toList = (input) => {
    if (Array.isArray(input)) {
      return input
        .map((item) => stripApBioInlineCitations(item))
        .filter((item) => item && !isApBioRemovedArtifactLabel(item));
    }
    return String(input || '')
      .split(/[\n,;]+/)
      .map((item) => stripApBioInlineCitations(item))
      .filter((item) => item && !isApBioRemovedArtifactLabel(item))
      .filter(Boolean);
  };

  // The AI may return answerKey / rubricNotes as objects (e.g. {a:"…", b:"…"} for
  // multi-part FRQs). Flatten them to a readable string before citation-stripping.
  const flattenField = (raw) => {
    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object') {
      // Array of strings → join with newlines
      if (Array.isArray(raw)) return raw.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join('\n');
      // Keyed object like { a: "...", b: "..." } → "a) ...\nb) ..."
      return Object.entries(raw)
        .map(([k, v]) => `${k}) ${typeof v === 'string' ? v : JSON.stringify(v)}`)
        .join('\n');
    }
    return String(raw);
  };

  return {
    question,
    answerKey: stripApBioInlineCitations(flattenField(value.answerKey || value.answer_key)),
    rubricNotes: stripApBioInlineCitations(flattenField(value.rubricNotes || value.rubric_notes)),
    skillFocus: toList(value.skillFocus || value.skill_focus || []),
    commandTerms: toList(value.commandTerms || value.command_terms || []),
    revealPolicy: String(value.revealPolicy || value.reveal_policy || 'after_attempt').trim() || 'after_attempt',
  };
}

export function normalizeApBioSessionData(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;

  const candidateBuckets = [];
  if (Array.isArray(parsed.conversations)) {
    candidateBuckets.push({
      conversations: parsed.conversations,
      activeChatId: parsed.activeChatId || null,
      fallbackWorkflow: AP_BIO_WORKFLOW_LEARNING,
    });
  }
  if (parsed.learning && Array.isArray(parsed.learning.conversations)) {
    candidateBuckets.push({
      conversations: parsed.learning.conversations,
      activeChatId: parsed.learning.activeChatId || null,
      fallbackWorkflow: AP_BIO_WORKFLOW_LEARNING,
    });
  }
  if (parsed['mark-scheme'] && Array.isArray(parsed['mark-scheme'].conversations)) {
    candidateBuckets.push({
      conversations: parsed['mark-scheme'].conversations,
      activeChatId: parsed['mark-scheme'].activeChatId || null,
      fallbackWorkflow: AP_BIO_WORKFLOW_FRQ,
    });
  }
  if (parsed.rubric && Array.isArray(parsed.rubric.conversations)) {
    candidateBuckets.push({
      conversations: parsed.rubric.conversations,
      activeChatId: parsed.rubric.activeChatId || null,
      fallbackWorkflow: AP_BIO_WORKFLOW_FRQ,
    });
  }

  const seen = new Set();
  const merged = [];

  candidateBuckets.forEach((bucket, bucketIndex) => {
    (bucket.conversations || []).forEach((conversation, conversationIndex) => {
      const normalizedConversation = normalizeApBioConversation(conversation, bucket.fallbackWorkflow);
      if (!normalizedConversation) return;
      const key = normalizedConversation.id != null
        ? `id:${normalizedConversation.id}`
        : `idx:${bucketIndex}:${conversationIndex}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(normalizedConversation);
    });
  });

  if (merged.length === 0) {
    return { conversations: [], activeChatId: null };
  }

  merged.sort((left, right) => {
    const leftId = Number(left?.id);
    const rightId = Number(right?.id);
    if (Number.isFinite(leftId) && Number.isFinite(rightId)) {
      return leftId - rightId;
    }
    return 0;
  });

  const preferredActiveChatId = candidateBuckets
    .map((bucket) => bucket.activeChatId)
    .find((value) => value != null);
  const hasActiveChat = merged.some((conversation) => String(conversation.id) === String(preferredActiveChatId));

  return {
    conversations: merged,
    activeChatId: hasActiveChat ? preferredActiveChatId : (merged[merged.length - 1]?.id || null),
  };
}

export function buildApBioSubmission({
  workflow,
  query,
  form,
  priorConversationCount = 0,
  conversationHistory = [],
}) {
  const normalizedWorkflow = normalizeApBioWorkflow(workflow);
  const trimmedQuery = String(query || '').trim();
  const questionText = String(form?.questionText || '').trim();
  const answerText = String(form?.answerText || '').trim();
  const questionLocator = String(form?.questionLocator || '').trim();
  const rubricContext = String(form?.rubricContext || '').trim();
  const analysisFocus = String(form?.analysisFocus || AP_BIO_DATA_FOCUS_OPTIONS[0].value).trim() || AP_BIO_DATA_FOCUS_OPTIONS[0].value;
  const practiceQuestionType = String(form?.practiceQuestionType || 'frq').trim().toLowerCase() === 'mcq' ? 'mcq' : 'frq';
  const practiceUnit = String(form?.practiceUnit || '').trim();
  const practiceDifficulty = ['support', 'challenge'].includes(String(form?.practiceDifficulty || '').trim().toLowerCase())
    ? String(form.practiceDifficulty).trim().toLowerCase()
    : 'exam';
  const revisionDays = toOptionalPositiveInt(form?.revisionDays);
  const revisionMinutes = toOptionalPositiveInt(form?.revisionMinutes);
  const weaknessTopics = splitApBioTopics(trimmedQuery);

  if (normalizedWorkflow === AP_BIO_WORKFLOW_FRQ) {
    const hasQuestionMaterial = Boolean(questionText || (Array.isArray(form?.questionAttachments) && form.questionAttachments.length > 0));
    const hasAnswerMaterial = Boolean(answerText || (Array.isArray(form?.answerAttachments) && form.answerAttachments.length > 0));
    const canSubmit = Boolean(hasQuestionMaterial && hasAnswerMaterial);
    return {
      workflow: normalizedWorkflow,
      route: 'stream',
      canSubmit,
      displayQuestion: [
        'FRQ Coach',
        questionText ? `Question:\n${questionText}` : 'Question: Attachment provided',
        questionLocator ? `Question location:\n${questionLocator}` : '',
        answerText ? `Your answer:\n${answerText}` : 'Your answer: Attachment provided',
        rubricContext ? `Rubric context:\n${rubricContext}` : '',
      ].filter(Boolean).join('\n\n'),
      message: [
        'Coach this AP Biology FRQ response.',
        questionText ? `Question prompt:\n${questionText}` : 'Question prompt is attached.',
        questionLocator ? `Question location:\n${questionLocator}` : '',
        answerText ? `Student answer:\n${answerText}` : 'Student answer is attached.',
        rubricContext ? `Scoring guideline or rubric context:\n${rubricContext}` : '',
      ].filter(Boolean).join('\n\n'),
      structuredFields: {
        questionContext: questionText || null,
        studentAnswer: answerText || null,
        questionLocator: questionLocator || null,
        rubricContext: rubricContext || null,
      },
    };
  }

  if (normalizedWorkflow === AP_BIO_WORKFLOW_DATA) {
    const focusLabel = getApBioDataFocusLabel(analysisFocus);
    return {
      workflow: normalizedWorkflow,
      route: 'stream',
      canSubmit: Boolean(trimmedQuery),
      displayQuestion: [
        'Data + Experiment Help',
        `Focus: ${focusLabel}`,
        trimmedQuery,
      ].filter(Boolean).join('\n\n'),
      message: [
        trimmedQuery,
        `Focus especially on ${focusLabel.toLowerCase()}.`,
      ].filter(Boolean).join('\n\n'),
      structuredFields: {
        analysisFocus,
      },
    };
  }

  if (normalizedWorkflow === AP_BIO_WORKFLOW_PRACTICE) {
    const prompt = trimmedQuery || `Generate one original AP Biology ${practiceQuestionType.toUpperCase()}${practiceUnit ? ` for ${practiceUnit}` : ''} at ${practiceDifficulty} difficulty.`;
    return {
      workflow: normalizedWorkflow,
      route: 'generate',
      canSubmit: true,
      displayQuestion: [
        'Practice Generator',
        `Type: ${practiceQuestionType.toUpperCase()}`,
        `Unit: ${practiceUnit || 'Any unit'}`,
        `Difficulty: ${practiceDifficulty}`,
        trimmedQuery ? `Topic / weakness:\n${trimmedQuery}` : '',
      ].filter(Boolean).join('\n\n'),
      prompt,
      questionType: practiceQuestionType,
      unit: practiceUnit || null,
      difficulty: practiceDifficulty,
      weaknessTopics,
    };
  }

  if (normalizedWorkflow === AP_BIO_WORKFLOW_REVISION) {
    const canSubmit = weaknessTopics.length > 0 || priorConversationCount > 0;
    return {
      workflow: normalizedWorkflow,
      route: 'stream',
      canSubmit,
      displayQuestion: [
        'Study Plan',
        weaknessTopics.length > 0 ? `Weak topics:\n${weaknessTopics.join(', ')}` : 'Use my recent AP Biology chat history.',
        revisionDays ? `Days until exam: ${revisionDays}` : '',
        revisionMinutes ? `Minutes per day: ${revisionMinutes}` : '',
      ].filter(Boolean).join('\n\n'),
      message: [
        weaknessTopics.length > 0
          ? `Create a focused AP Biology study plan for these weak topics: ${weaknessTopics.join(', ')}.`
          : 'Create a focused AP Biology study plan using the recent conversation context.',
        revisionDays ? `Days until exam: ${revisionDays}.` : '',
        revisionMinutes ? `Minutes available per day: ${revisionMinutes}.` : '',
      ].filter(Boolean).join('\n'),
      structuredFields: {
        studyHorizonDays: revisionDays,
        minutesPerDay: revisionMinutes,
        weaknessTopics,
      },
    };
  }

  const followUpContext = normalizedWorkflow === AP_BIO_WORKFLOW_LEARNING
    && isContextDependentApBioFollowUp(trimmedQuery)
    ? buildApBioFollowUpContext(conversationHistory)
    : null;

  return {
    workflow: AP_BIO_WORKFLOW_LEARNING,
    route: 'stream',
    canSubmit: Boolean(trimmedQuery),
    displayQuestion: trimmedQuery,
    message: trimmedQuery,
    structuredFields: followUpContext ? { followUpContext } : {},
  };
}
